export interface ApiError {
  error: { code: string; message?: string; status?: number; issues?: unknown; detail?: unknown };
}

export class ApiCallError extends Error {
  status: number;
  payload: ApiError | undefined;
  constructor(status: number, payload: ApiError | undefined, fallbackMessage: string) {
    super(payload?.error?.message ?? fallbackMessage);
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try { json = JSON.parse(text); } catch { json = text; }
  }
  if (!res.ok) {
    throw new ApiCallError(res.status, json as ApiError | undefined, `${init?.method ?? 'GET'} ${path} failed (${res.status})`);
  }
  return json as T;
}

export const api = {
  get:   <T = unknown>(path: string)            => request<T>(path),
  post:  <T = unknown>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T = unknown>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put:   <T = unknown>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del:   <T = unknown>(path: string)            => request<T>(path, { method: 'DELETE' }),
};

// -------- Types (subset of Autotask shapes) --------

export interface Ticket {
  id: number;
  ticketNumber: string;
  title: string;
  status: number;
  priority: number;
  queueID: number | null;
  companyID: number | null;
  assignedResourceID: number | null;
  assignedResourceRoleID: number | null;
  dueDateTime: string | null;
  createDate: string | null;
  lastActivityDate: string | null;
  ticketType: number | null;
  issueType: number | null;
  subIssueType: number | null;
  /** List endpoints cap this at ~1,500 chars (GET /tickets); the ticket page has the full text. */
  description: string | null;
}

export interface PicklistValue {
  value: string;
  label: string;
  isDefaultValue?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  /** For child picklists (e.g. subIssueType), references the parent picklist value. */
  parentValue?: string;
}

export interface Picklists {
  status: PicklistValue[];
  priority: PicklistValue[];
  queueID: PicklistValue[];
  ticketType: PicklistValue[];
  source: PicklistValue[];
  issueType: PicklistValue[];
  subIssueType: PicklistValue[];
}

export interface TicketNote {
  id: number;
  ticketID: number;
  title: string;
  description: string;
  noteType: number;
  publish: number;
  createDateTime: string;
  creatorResourceID: number | null;
  createdByContactID: number | null;
}

export interface TimeEntry {
  id: number;
  ticketID: number;
  resourceID: number;
  hoursWorked: number;
  summaryNotes?: string;
  internalNotes?: string;
  dateWorked: string;
  createDateTime?: string;
  isNonBillable?: boolean;
  showOnInvoice?: boolean;
}

/** Response from POST /api/tickets/:id/time — the split time entry + customer note. */
export interface TimeEntryResult {
  itemId?: number;
  /** id of the customer-visible note created from the summary, or null if none was sent. */
  noteId?: number | null;
  /** Set when the time entry saved but posting the customer note failed. */
  noteError?: string | null;
}

export interface Attachment {
  id: number;
  parentID: number;
  title?: string;
  fullPath?: string;
  publish?: number;
  contentType?: string;
  attachType?: string;
  fileSize?: number;
}

export interface Opportunity {
  id: number;
  title: string;
  status: number;
  stage: number | null;
  probability: number | null;
  amount: number | null;
  ownerResourceID: number | null;
  companyID: number | null;
  projectedCloseDate: string | null;
  createDate: string | null;
  lastActivity: string | null;
  description: string | null;
  userDefinedFields?: { name: string; value: string | null }[];
}

export interface OpportunityDetail extends Opportunity {
  contactID: number | null;
  nextStep: string | null;
  barriers: string | null;
  helpNeeded: string | null;
  lastActivity: string | null;
  closedDate: string | null;
  leadSource: number | null;
  rating: number | null;
  salesProcessPercentComplete: number | null;
}

export interface OppPicklists {
  stage: PicklistValue[];
  status: PicklistValue[];
  leadSource: PicklistValue[];
  rating: PicklistValue[];
  serviceCategory: PicklistValue[];
}

export interface QueryResult<T> {
  items: T[];
  pageDetails?: unknown;
  /** Set by the ticket-detail route when a child query failed, so the UI can say so. */
  error?: string;
}

export interface TicketDetailResponse {
  ticket: { item: any };
  notes: QueryResult<TicketNote>;
  time: QueryResult<TimeEntry>;
  attachments: QueryResult<Attachment>;
}

export interface TicketHistoryEntry {
  id: number;
  ticketID: number;
  action: string;
  detail: string;
  date: string;
  resourceID: number | null;
}

export interface OpportunityNote {
  id: number;
  name: string | null;
  note: string | null;
  createDateTime: string;
  actionType: number;
}

// -------- Dashboards (payloads mirror server/dashboards.ts) --------

export type DashboardScope = 'me' | 'team';

export interface DashboardBucket {
  id: number | string;
  label: string;
  value: number;
  color?: string;
}

export interface ServiceDeskDashboard {
  scope: DashboardScope;
  generatedAt: string;
  kpis: { open: number; unassigned: number; overdue: number; createdToday: number; completedToday: number };
  byStatus: DashboardBucket[];
  byQueue: DashboardBucket[];
  byPriority: DashboardBucket[];
  byAssignee: DashboardBucket[];
  ageBuckets: DashboardBucket[];
  trend: { weekStart: string; created: number; completed: number }[];
}

export interface SalesDashboard {
  scope: DashboardScope;
  generatedAt: string;
  kpis: {
    openPipeline: number;
    weightedForecast: number;
    openCount: number;
    wonThisMonth: number;
    wonThisMonthCount: number;
    avgDealSize: number;
    winRate: number | null;
  };
  pipelineByStage: DashboardBucket[];
  byOwner: DashboardBucket[];
  closingSoon: { id: number; title: string; amount: number; projectedCloseDate: string }[];
  wonTrend: { month: string; value: number; count: number }[];
  createdTrend: { month: string; count: number }[];
}

// -------- Admin types --------

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  autotaskResourceId: number | null;
  createdAt: string;
  lastLogin: string | null;
  providers: string[];
}

export interface WebhookLogEntry {
  id: number;
  receivedAt: string;
  eventType: string | null;
  ticketId: number | null;
  ticketNumber: string | null;
  title: string | null;
  companyId: number | null;
  status: number | null;
  priority: number | null;
  rawPayload: string;
}

export interface AdminSystemInfo {
  uptimeSeconds: number;
  sseClients: number;
  nodeVersion: string;
  environment: string;
  envVarStatus: Record<string, boolean>;
}

/** Which assistant a prompt belongs to. 'ticket' = Engineering AI, 'sales' = Sales AI, 'project' = Projects AI. */
export type AiPromptId = 'ticket' | 'sales' | 'project';

export interface AiPromptSettings {
  id: AiPromptId;
  label: string;          // display name, e.g. "Engineering AI"
  systemPrompt: string;   // the effective prompt (override if set, else the default)
  isCustom: boolean;      // whether an admin override is in effect
  updatedAt: string | null;
  default: string;        // the built-in default, for "reset"
}

export interface AiSettings {
  model: string;
  configured: boolean;
  prompts: Record<AiPromptId, AiPromptSettings>;
}

/** A reusable ticket-note body. Private to its owner unless `isShared`. */
export interface NoteTemplate {
  id: number;
  title: string;
  body: string;
  isShared: boolean;
  /** Pre-ticks the note form's "Internal" checkbox when inserted. */
  isInternal: boolean;
  /** Only the owner may edit or delete. */
  isOwner: boolean;
  ownerName: string;
  createdAt: string;
  updatedAt: string;
}

export interface NoteTemplateInput {
  title: string;
  body: string;
  isShared: boolean;
  isInternal: boolean;
}
